import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
  type MutableRefObject,
} from 'react';
import { OnChangeFunction, Option } from '../..';
import DropdownList from '../DropdownList';
import useOutsideClick from '~/hooks/useClickOutside';
import isCursorInBoundary from '~/util/isCursorInBoundary';
import { isNil } from 'ramda';
import getClosestScrollableParent from '~/util/getClosestScrollableParent';
import useKeyBindings from './hooks/useKeyBindings';
import PortalledPositioner from '~/components/molecule/PortalledPositioner';
import type { RootId } from '~/components/molecule/Portal';

export type Props = {
  options: Array<Option>;
  selectedOptionIdx?: number | null;
  dropdownListOpen: boolean;

  /** Select the first option by default */
  withDefaultFirstOption?: boolean;

  /** Loading state shown in the dropdown list */
  loading?: boolean;
  dropdownWidth?: number;

  /** Index of the option to scroll to when dropdown opens */
  indexToDisplayOnOpen?: number;

  /** Goes to the container */
  className?: string;

  /**
   * The component that opened the dropdown list. A button or a dropdown etc.
   */
  openerRef: MutableRefObject<HTMLElement | null>;

  /** The root id of the portalled positioner */
  positionerRootId?: RootId;

  /** Called when option is selected with keybindings or option click */
  onChange: OnChangeFunction;

  /** Called on escape key press */
  onClose?: () => void;

  /** Called when click is outside the list */
  onClickOutside?: (e?: MouseEvent) => void;
};

const DropdownListContainer: React.FCC<Omit<Props, 'dropdownListOpen'>> = ({
  dataTestId,
  options,
  withDefaultFirstOption,
  loading,
  dropdownWidth,
  indexToDisplayOnOpen,
  openerRef,
  positionerRootId,
  onChange,
  onClose,
  onClickOutside,
  ...rest
}) => {
  const [openerRect, setOpenerRect] = useState<DOMRect | null>(null);

  const selected =
    rest.selectedOptionIdx && rest.selectedOptionIdx >= 0
      ? rest.selectedOptionIdx
      : withDefaultFirstOption
        ? 0
        : null;

  const [selectedOptionIdx, setSelectedOptionIdx] = useState<number | null>(
    selected,
  );

  const listRef = useRef<HTMLUListElement>(null);

  const [listSize, setListSize] = useState({
    width: 0,
    height: 0,
  });

  const rect = openerRef.current?.getBoundingClientRect();
  const listRect = listRef.current?.getBoundingClientRect();
  const scrollableParent = getClosestScrollableParent(openerRef.current);

  const rectMemo = useMemo(
    () => rect,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rect?.width, rect?.height, rect?.top, rect?.bottom],
  );
  const listRectMemo = useMemo(
    () => listRect,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listRect?.width, listRect?.height],
  );

  // Scroll to the specified index when the dropdown opens
  useEffect(() => {
    if (
      listRef.current &&
      !isNil(indexToDisplayOnOpen) &&
      indexToDisplayOnOpen >= 0 &&
      indexToDisplayOnOpen < options.length
    ) {
      // Use setTimeout to ensure the list is fully rendered
      setTimeout(() => {
        const listElement = listRef.current;
        if (!listElement) return;

        // Find the option element to scroll to
        const optionElements = listElement.querySelectorAll(
          '[data-option-index], li',
        );
        const targetOption = optionElements[
          indexToDisplayOnOpen
        ] as HTMLElement;

        if (targetOption) {
          // Calculate the scroll position to center the target option
          const listHeight = listElement.clientHeight;
          const optionHeight = targetOption.offsetHeight;
          const optionTop = targetOption.offsetTop;

          // Center the option in the visible area
          const scrollTop = optionTop - listHeight / 2 + optionHeight / 2;

          listElement.scrollTop = Math.max(0, scrollTop);
        }
      }, 0);
    }
  }, [indexToDisplayOnOpen, options.length]);

  useLayoutEffect(() => {
    const handleResize = () => {
      setOpenerRect(rect || null);

      if (listRect)
        setListSize({ width: listRect.width, height: listRect.height });
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    if (scrollableParent)
      scrollableParent.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);

      if (scrollableParent)
        scrollableParent.removeEventListener('scroll', handleResize);
    };
    // Pass memoized rects otherwise they cause infinite rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openerRef, scrollableParent, rectMemo, listRectMemo]);

  useOutsideClick(listRef, e => {
    if (isNil(e) || isNil(openerRect) || isNil(onClickOutside)) return;

    const isClickOnOpener = isCursorInBoundary({
      event: e,
      boundary: openerRect,
    });
    if (isClickOnOpener) return;
    onClickOutside(e);
  });

  useKeyBindings({
    onClose,
    onSelect: optionIdx => setSelectedOptionIdx(optionIdx),
    selectedOptionIdx,
    options,
    onChange,
  });

  return (
    <>
      <PortalledPositioner
        rootId={positionerRootId || 'dropdown-portal'}
        openerRect={openerRect}
        listSize={listSize}
        dataTestId={dataTestId}
      >
        <DropdownList
          dropdownWidth={dropdownWidth || 0}
          loading={loading}
          options={options}
          onChange={onChange}
          actuallySelected={rest.selectedOptionIdx}
          selectedOptionIdx={selectedOptionIdx}
          onClickOutside={onClickOutside}
          ref={listRef}
        />
      </PortalledPositioner>
    </>
  );
};

const DropdownListFacade: React.FCC<Props> = props => {
  if (!props.dropdownListOpen) return null;

  return <DropdownListContainer {...props} />;
};

export default DropdownListFacade;
